import type { CrmCustomFiltersDescription } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-options";
import type { BaseCrmIntegrationObject } from "app/pages/sales-intelligence/domain/crm-integration/types/object-type";
import type { DynamicFilterFieldDescription } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-options";
import dynamicFiltersAdapter from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/dynamic-filters-adapter";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type {
    CrmCustomFilterFieldEntry,
    CrmCustomFilterGroupEntry,
    CrmFiltersListState,
    ICrmCustomFiltersListViewModel,
} from "../../types/filters-view-model";

type Deps = {
    readonly description?: CrmCustomFiltersDescription;
    readonly integrationObject?: BaseCrmIntegrationObject;
    readonly isEnabled: boolean;
    readonly isNotConnected: boolean;
    readonly isLoading: boolean;
};

export default function createCrmCustomFiltersListViewModel(deps: Deps): ICrmCustomFiltersListViewModel {
    const toFieldEntry = (field: DynamicFilterFieldDescription): CrmCustomFilterFieldEntry | undefined => {
        // Per-field boundary: `toDescriptor` throws on an out-of-contract field; track and drop the field
        // so a single malformed field never crashes the whole filter
        try {
            const descriptor = dynamicFiltersAdapter.toDescriptor(field);

            if (!descriptor) {
                return undefined;
            }

            return { descriptor };
        } catch (error) {
            trackError(
                error instanceof Error
                    ? error
                    : new SiError("Failed to build CRM filter descriptor", {
                          category: errorCategoryMap.invariant,
                          operation: "toFieldEntry",
                      }),
            );

            return undefined;
        }
    };

    const buildEntries = (
        description: CrmCustomFiltersDescription | undefined,
        integrationName: BaseCrmIntegrationObject | undefined,
    ): readonly CrmCustomFilterGroupEntry[] => {
        if (!description || !integrationName) {
            return [];
        }

        const integrationGroup = description.integrationCustomFilterGroups.find(
            (entry) =>
                entry.integrationName === integrationName.integrationName &&
                entry.objectType === integrationName.objectType,
        );

        if (!integrationGroup) {
            return [];
        }

        return integrationGroup.filters
            .map((group): CrmCustomFilterGroupEntry | undefined => {
                const fields = group.fields
                    .map(toFieldEntry)
                    .filter((field): field is CrmCustomFilterFieldEntry => !is.undefined(field));

                return fields.length > 0
                    ? { id: group.id, name: group.name, widget: group.widget, inclusions: group.inclusions, fields }
                    : undefined;
            })
            .filter((entry): entry is CrmCustomFilterGroupEntry => !is.undefined(entry));
    };

    const resolveListState = ({ isEnabled, isLoading, isNotConnected }: Deps): CrmFiltersListState => {
        if (!isEnabled) {
            return "hidden";
        }

        if (isLoading) {
            return "loading";
        }

        if (isNotConnected) {
            return "notConnected";
        }

        return "ready";
    };

    const entries = buildEntries(deps.description, deps.integrationObject);

    return {
        state: resolveListState(deps),
        integrationObject: deps.integrationObject,
        entries,
    };
}
